import React, { useState } from 'react';
import { Percent, Info } from 'lucide-react';
import { useEstimatedFees } from '../hooks/useEstimatedFees';
import BrokerFeesModal from './BrokerFeesModal';

/**
 * Compact READ-ONLY display of the user's "estimated broker fees %".
 * The value is provided by the backend (admin setting / user default) and is
 * NO LONGER editable from the UI. It is only shown for reference so users can
 * see how it affects their R:R / P&L projections.
 *
 * Props:
 *   - showLabel (default true): render the "Frais estimés" label
 *   - showModalTrigger (default true): render an "En savoir plus" info icon
 *     that opens the BrokerFeesModal
 *   - compact (default false): tighter layout for embedding in cards
 *   - baseValue (optional number): if provided, renders a small right-side
 *     "net = base − fees%" preview (e.g. adjusted R:R or adjusted %ROI). The
 *     component NEVER mutates parent state — it only shows the projection.
 *   - baseLabel (default 'net'): label for the preview
 *   - baseFormatter (optional fn): custom formatter for the preview value
 *   - baseKind (default 'percent'): 'percent' | 'ratio' | 'currency' — controls
 *     how the deduction is applied ('percent' subtracts fees%, 'ratio' applies
 *     `base * (1 - fees/100)`, 'currency' subtracts `abs(base) * fees / 100`)
 */
export default function EstimatedFeesField({
  showLabel = true,
  showModalTrigger = true,
  compact = false,
  baseValue = null,
  baseLabel = 'net',
  baseFormatter = null,
  baseKind = 'percent',
  className = '',
  'data-testid': dataTestId = 'estimated-fees-field',
}) {
  const { fees, loading } = useEstimatedFees();
  const [showModal, setShowModal] = useState(false);

  // Compute the preview value if a baseValue was provided
  let previewText = null;
  if (baseValue !== null && baseValue !== undefined && Number.isFinite(Number(baseValue))) {
    const base = Number(baseValue);
    let net;
    if (baseKind === 'ratio') {
      net = base * (1 - fees / 100);
    } else if (baseKind === 'currency') {
      const deduction = Math.abs(base) * (fees / 100);
      net = base >= 0 ? base - deduction : base - deduction;
    } else {
      net = base - fees;
    }
    if (baseFormatter) {
      previewText = baseFormatter(net, base);
    } else if (baseKind === 'ratio') {
      previewText = `${baseLabel} ${net.toFixed(2)}:1`;
    } else if (baseKind === 'currency') {
      previewText = `${baseLabel} ${net >= 0 ? '+' : ''}${net.toFixed(2)} $`;
    } else {
      previewText = `${baseLabel} ${net >= 0 ? '+' : ''}${net.toFixed(2)}%`;
    }
  }

  const displayValue = loading ? '…' : Number(fees).toFixed(2);

  return (
    <>
      <div
        className={`inline-flex items-center gap-2 flex-wrap ${compact ? 'text-xs' : 'text-sm'} ${className}`}
        data-testid={dataTestId}
      >
        {showLabel && (
          <span className="text-muted-foreground whitespace-nowrap flex items-center gap-1">
            <Percent className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
            Frais estimés
          </span>
        )}
        <span
          className={`inline-flex items-center justify-end min-w-[3.75rem] pr-2 pl-2 ${compact ? 'h-7 text-xs' : 'h-8 text-sm'} rounded-md border border-white/10 bg-background/40 font-mono text-foreground/90 select-none cursor-default`}
          aria-label={`Frais estimés fixés à ${displayValue}%`}
          title="Cette valeur est fixée par la plateforme (non modifiable)."
          data-testid={`${dataTestId}-value`}
        >
          {displayValue}
          <span className={`ml-0.5 text-muted-foreground ${compact ? 'text-[10px]' : 'text-xs'}`}>%</span>
        </span>
        {previewText && (
          <span
            className={`font-mono text-emerald-400 whitespace-nowrap ${compact ? 'text-xs' : 'text-sm'}`}
            data-testid={`${dataTestId}-preview`}
          >
            {previewText}
          </span>
        )}
        {showModalTrigger && (
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="En savoir plus sur les frais broker"
            data-testid={`${dataTestId}-info-btn`}
          >
            <Info className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
          </button>
        )}
      </div>
      {showModal && <BrokerFeesModal onClose={() => setShowModal(false)} />}
    </>
  );
}
